// Sugar Alarm — cycle 435: ONE selector owns the current glucose number,
// and it samples its own clock.
//
// ⚠ WHY THIS FILE EXISTS. Two production incidents on 2026-09-14, same
// household, two mechanisms, and a fix for either alone leaves the product
// exposed:
//
//   • 20:27Z — a phone displayed 76 while the member read 181. The 76 was
//     that member's own reading from 13:47:22Z, six hours forty minutes
//     earlier. It was a THAW, not a reload: the app had been frozen with the
//     value in memory, every client-side input was frozen with it — the
//     ticking `useNow` clock included — and on resume the first paint judged
//     the reading against a `now` that was still 13:47. Nothing on the
//     screen could tell, because the clock it was told to compare against
//     had stopped with the number.
//
//   • 16:26Z — a 33-minute provider outage. The server's newest reading was
//     31 minutes old and the SERVER'S OWN `stale` bit was true on every
//     delivery. The client kept only `reading` and threw the verdict away,
//     so a live clock would not have helped: only the server's verdict,
//     discarded on arrival, could have blanked it.
//
// THE CONTRACT. The app displays a number it can PROVE is current, or it
// displays nothing. Never a third state in which a stale number looks live.
// Three rules, in this order:
//
//   1. THE SELECTOR SAMPLES ITS OWN CLOCK. `nowMs` is `Date.now()` taken at
//      selection time by the caller — never a ticking React state, never a
//      prop that was computed on an earlier render. That is what makes the
//      fix hold on a thaw where every other input is frozen. A resume
//      listener that resets the ticking clock is an optimisation on top of
//      this, never the fix.
//
//   2. SERVER TRUTH OUTRANKS CLIENT COMPUTATION, AND THE CLIENT CAN ONLY
//      TIGHTEN. If the server said `stale` about this reading, it is stale
//      and no client arithmetic may override it. If the server said fresh,
//      the client's own age check against the sampled clock may still call
//      it stale, because time passes after delivery.
//
//   3. STREAM CONFIRMATION IS NARROW AND PER MEMBER. A confirmation is a
//      server-authored statement about THIS MEMBER's newest reading: a
//      successful `/api/glucose/latest`, a reconnect snapshot row, or a
//      `glucose` frame that is the newest applied reading for the member.
//      NOT a pong, not socket OPEN, not an alert frame, not a snooze or
//      treating response, not a dashboard status, not a local timer, not a
//      cached reading. A failed REST reseed renews nothing. A successful
//      REST answer carrying NO reading DOES confirm — it establishes that
//      the server currently has no reading, which is a confirmed state.
//
// ⚠ THIS IS A DISPLAY RULE, NEVER AN ALARM RULE. Nothing here gates a
// takeover, a banner, a control or a sound. A member in an alarm state whose
// reading has gone unprovable still shows the alarm surface, its controls,
// and still sounds; only the NUMBER becomes unknown. No alarm-path module
// imports this file — swept by `__tests__/readingUnknown.test.ts` and
// enforced by the AST guard in `__tests__/currentGlucoseGuard.test.ts`.
//
// ⚠ The unit of every comparison is SECONDS. The server compares
// `age_seconds > threshold` exactly; a value floored to whole minutes first
// passes as fresh for up to 59 seconds after the server has called it stale.

import { ageSeconds, staleSecondsFor } from "./glucose";
import type { Reading, Trend } from "./types";

/** Which server statement confirmed the member's newest reading. */
export type ConfirmationSource = "rest_latest" | "snapshot" | "glucose_frame";

/** Per-member proof that the server told this client what the member's
 *  newest reading is. `readingTimestampMs` is the sample time of the reading
 *  the statement carried — `null` when the statement was "no reading". */
export interface StreamConfirmation {
  receivedAtMs: number;
  readingTimestampMs: number | null;
  source: ConfirmationSource;
}

/** The server's own verdict about a reading, as delivered with it.
 *  `readingTimestampMs` names the reading it judged, so a verdict about an
 *  older sample is never applied to a newer one. */
export interface ServerVerdict {
  stale: boolean;
  ageSeconds: number | null;
  receivedAtMs: number;
  readingTimestampMs: number | null;
}

export type CurrentGlucoseStatus = "current" | "stale" | "unconfirmed" | "empty";

export type CurrentGlucoseReason =
  | "current"
  | "no_member"
  | "never_confirmed"
  | "confirmation_expired"
  /** The newest server statement about this member did not name the
   *  sample the client is holding — a retained frame copy after the server
   *  said "no reading", or an older backfilled row applied over a newer
   *  confirmed one. The held value is not the newest, so it is not
   *  current. */
  | "not_newest"
  | "no_reading"
  | "server_stale"
  | "sample_old";

declare const displayGlucoseBrand: unique symbol;

/** A glucose value that has passed the selector and may be PRESENTED as
 *  current. The brand is the enforcement: a current-value component types
 *  its prop as `DisplayGlucose | null` and a plain `Reading` cannot be
 *  handed to it, so no surface can paint a number the selector did not
 *  release. Only `selectCurrentGlucose` constructs one. */
export interface DisplayGlucose {
  readonly [displayGlucoseBrand]: true;
  readonly sgv: number;
  readonly trend: Trend | null;
  readonly trendRate: number | null;
  readonly provider: string;
  readonly timestampMs: number;
  readonly classification: string | null;
}

export interface CurrentGlucoseView {
  status: CurrentGlucoseStatus;
  reason: CurrentGlucoseReason;
  memberId: string | null;
  /** The raw reading the decision was made about, for secondary fields
   *  (age label, trend of a withheld value, the Live Activity end rule).
   *  NEVER the number to paint — that is `displayValue`. */
  reading: Reading | null;
  /** Present ONLY when `status === "current"`. */
  displayValue: DisplayGlucose | null;
  ageMinutes: number | null;
  ageSeconds: number | null;
  /** The sample's own age passes the provider rule and the server did not
   *  call it stale. */
  sampleFresh: boolean;
  /** A confirmation exists and is inside `unknownAfterMs`. */
  serverConfirmed: boolean;
  confirmationAgeMs: number | null;
}

export interface SelectCurrentGlucoseInput {
  memberId: string | null;
  reading: Reading | null;
  provider: string | null | undefined;
  /** ⚠ `Date.now()` sampled by the caller at selection time. Never a
   *  ticking value. */
  nowMs: number;
  serverVerdict: ServerVerdict | null | undefined;
  confirmation: StreamConfirmation | null | undefined;
  unknownAfterMs: number;
}

function tsMs(reading: Reading | null): number | null {
  if (reading == null) return null;
  const ms = new Date(reading.timestamp).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function brand(reading: Reading, timestampMs: number): DisplayGlucose {
  return {
    sgv: reading.sgv,
    trend: reading.trend,
    trendRate: reading.trend_rate,
    provider: reading.provider,
    timestampMs,
    classification: reading.classification ?? null,
  } as DisplayGlucose;
}

function view(
  partial: Omit<CurrentGlucoseView, "ageMinutes" | "ageSeconds"> & {
    ageSeconds: number | null;
  },
): CurrentGlucoseView {
  return {
    ...partial,
    ageMinutes: partial.ageSeconds == null ? null : Math.floor(partial.ageSeconds / 60),
  };
}

/** THE selector. Pure: same inputs, same answer. Order matters and is the
 *  contract — confirmation first (on a thaw it does not wait for the
 *  network), then emptiness, then the server's verdict, then the client's
 *  own age check, which may only tighten. */
export function selectCurrentGlucose(input: SelectCurrentGlucoseInput): CurrentGlucoseView {
  const { memberId, reading, nowMs, unknownAfterMs } = input;
  const readingTs = tsMs(reading);
  const age = readingTs == null ? null : ageSeconds(readingTs, nowMs);
  const confirmation = input.confirmation ?? null;
  const confirmationAgeMs = confirmation == null ? null : nowMs - confirmation.receivedAtMs;
  const base = {
    memberId,
    reading,
    displayValue: null,
    ageSeconds: age,
    sampleFresh: false,
    serverConfirmed: false,
    confirmationAgeMs,
  };

  if (memberId == null) {
    return view({ ...base, status: "empty", reason: "no_member" });
  }

  // 1. Confirmation. `null` = nothing has ever confirmed this member: unknown
  //    by definition. A confirmation older than the bound is one whose
  //    renewal has demonstrably failed (the bound already includes one full
  //    reseed allowance — `readingFreshness.ts`). ⚠ Judged against the
  //    SAMPLED clock, before any fetch: on a thaw this is the line that
  //    withholds the number. A negative age (a clock that ran backwards)
  //    reads as confirmed, exactly as `readingUnknown` always did — a wrong
  //    clock must not blank a live number.
  if (confirmation == null) {
    return view({ ...base, status: "unconfirmed", reason: "never_confirmed" });
  }
  // A NaN bound would make `> unknownAfterMs` false for every age and the
  // number would never expire — the bound fails CLOSED instead (0: only a
  // same-instant confirmation counts). `Infinity` is a legitimate bound
  // (the synthetic preview) and is kept.
  const bound = Number.isNaN(unknownAfterMs) ? 0 : unknownAfterMs;
  if (confirmationAgeMs != null && confirmationAgeMs > bound) {
    return view({ ...base, status: "unconfirmed", reason: "confirmation_expired" });
  }
  const confirmed = { ...base, serverConfirmed: true };

  // 2. Emptiness. A confirmed "no reading" is a confirmed state, not an
  //    absent one: the server currently has nothing for this member.
  if (reading == null || readingTs == null) {
    return view({ ...confirmed, status: "empty", reason: "no_reading" });
  }

  // 2b. The confirmation must NAME this sample. The newest server statement
  //     about the member carries the sample time it was about; if the
  //     client is holding a different sample — a frame copy retained after
  //     the server answered "no reading", or an older backfilled row the
  //     transport applied over a newer confirmed one — the held value is
  //     not the newest and cannot be presented as current. (test-author's
  //     finding on the non-active snapshot path, and the security review's
  //     LOW on the verdict that could never apply.)
  if (confirmation.readingTimestampMs !== readingTs) {
    return view({ ...confirmed, status: "unconfirmed", reason: "not_newest" });
  }

  // 3. The server's verdict about THIS reading outranks anything below. A
  //    verdict about a different sample (an older one, judged before this
  //    reading arrived) is not evidence about this one and is ignored.
  const verdict = input.serverVerdict ?? null;
  if (verdict != null && verdict.stale && verdict.readingTimestampMs === readingTs) {
    return view({ ...confirmed, status: "stale", reason: "server_stale" });
  }

  // 4. The client's own age check, in seconds, against the sampled clock —
  //    it can only make a server-fresh reading stale, never the reverse.
  const provider = reading.provider || input.provider || null;
  if (age != null && age > staleSecondsFor(provider)) {
    return view({ ...confirmed, status: "stale", reason: "sample_old" });
  }

  return view({
    ...confirmed,
    status: "current",
    reason: "current",
    sampleFresh: true,
    displayValue: brand(reading, readingTs),
  });
}

/** The socket-state fields the selector reads. Structural, so this module
 *  never imports the hook; `GlucoseSocketState` satisfies it. */
export interface CurrentGlucoseSource {
  activeMemberId: string | null;
  lastReading: Reading | null;
  byMember: Record<string, { lastReading: Reading | null } | undefined>;
  dashboardCards: readonly { member_id: string; latest_reading: Reading | null; cgm_provider: string | null }[];
  confirmationByMember: Record<string, StreamConfirmation | undefined>;
  verdictByMember: Record<string, ServerVerdict | undefined>;
  readingUnknownAfterMs: number;
}

/** Select the current glucose for one member out of the socket state.
 *  `nowMs` defaults to `Date.now()` SAMPLED HERE, at the call — the default
 *  is the contract, and a caller passing a ticking value instead is the
 *  defect this cycle exists to remove. */
export function selectCurrentGlucoseFor(
  socket: CurrentGlucoseSource | null | undefined,
  memberId: string | null,
  nowMs: number = Date.now(),
): CurrentGlucoseView {
  if (socket == null || memberId == null) {
    return selectCurrentGlucose({
      memberId: null,
      reading: null,
      provider: null,
      nowMs,
      serverVerdict: null,
      confirmation: null,
      unknownAfterMs: socket?.readingUnknownAfterMs ?? 0,
    });
  }
  const card = socket.dashboardCards.find((c) => c.member_id === memberId);
  // The ACTIVE member's reading is the top-level mirror and only that: it
  // is the one field every delivery path writes for the active member — a
  // REST reseed included, which writes nothing into `byMember`. Reading
  // `byMember` first there would let a reseed that answered "no reading"
  // be overridden by the older copy the frames left behind. A non-active
  // member has only its frames and the dashboard card.
  const reading =
    memberId === socket.activeMemberId
      ? socket.lastReading
      : (socket.byMember[memberId]?.lastReading ?? card?.latest_reading ?? null);
  return selectCurrentGlucose({
    memberId,
    reading,
    // `||`, not `??`: an EMPTY provider string on a reading must fall back
    // to the member's connected CGM rather than mask it and land on the
    // looser 15-minute default (test-author's note).
    provider: reading?.provider || card?.cgm_provider || null,
    nowMs,
    serverVerdict: socket.verdictByMember[memberId] ?? null,
    confirmation: socket.confirmationByMember[memberId] ?? null,
    unknownAfterMs: socket.readingUnknownAfterMs,
  });
}

/** The household's "needs attention" count asked, before this cycle,
 *  whether the member's LAST SAMPLE was out of range — regardless of its
 *  freshness. That is not a number to paint, so it is not the selector's
 *  question; it is answered here, in the lib, so no presentation module
 *  reads a raw value. A CURRENT out-of-range value counts; a stale or
 *  withheld one whose last sample was out of range still counts, exactly
 *  as it did (regression-guard: the count had silently narrowed). Fixed
 *  70 / 180 as before — this is a headline count, not the zone rule. */
export function lastSampleOutOfRange(view: CurrentGlucoseView): boolean {
  const sgv = view.displayValue?.sgv ?? view.reading?.sgv ?? null;
  if (sgv == null) return false;
  return sgv < 70 || sgv > 180;
}
